<template>
  <div class="page-content delivery-search">
    <delivery-item-details
      :is-show-dialog="status.isOptionsModalVisible"
      :is-item-update="status.isItemUpdate"
      :pending-item="pendingItem"
      :item="item"
      @cancel="modalClose"
      @submit="optionsFormSubmit"
    />

    <store-search
      v-show="step == 'search'"
      :is-delivery-estimate="isDeliveryEstimate"
      :visible-stores-limit="visibleStoresLimit"
      @select-store="onSelectStore"
      @set-address="onSetAddress"
      @set-delivery-instructions="onSetDeliveryInstructions"
      @set-total-stores="$emit('set-total-stores', $event)"
    />

    <store-items
      v-show="step == 'store'"
      :is-delivery-estimate="isDeliveryEstimate"
      :store-group="selectedStoreGroup"
      :delivery-address="deliveryAddress"
      :cart="cart"
      :estimate="estimate"
      :is-estimate-loading="status.isEstimateLoading"
      :pending-item="pendingItem"
      @clear-store="onClearStore"
      @clear-cart="clearCart"
      @cancel-estimate="cancelEstimate"
      @item-options="showItemOptions"
      @checkout="step = 'checkout'"
    />

    <store-checkout
      v-show="step == 'checkout'"
      :defaultTip="savedDefaultTip"
      :is-delivery-estimate="isDeliveryEstimate"
      :store-group="selectedStoreGroup"
      :delivery-address="deliveryAddress"
      :all-items-from-the-same-menu="getMenuIds(estimate).length === 1"
      :resolve-order="resolveOrder"
      :cart="cart"
      :copy-order="copyOrder"
      :additional-charges="additionalCharges"
      :contact-info="contactInfo"
      :estimate="estimate"
      :order-options="orderOptions"
      :is-estimate-loading="status.isEstimateLoading"
      :estimate-failed="status.estimateFailed"
      :charge-options="chargeOptions"
      :plan-name="planName"
      @clear-checkout="step = 'store'"
      @clear-cart="clearCart"
      @item-options="showItemOptions"
      @remove-item="removeItem"
      @refresh-estimate="refreshEstimate"
      @change-charge-options="onChangeChargeOptions"
      @change-plan="onChangePlan"
    />
  </div>
</template>

<script lang="ts">
import { ref, Ref, reactive, watch, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { get, map, uniq } from 'lodash';
import { ElMessageBox } from 'element-plus';
import delay from 'yoctodelay';
import events from '@/events';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useGourmet } from '@/use/gourmet';
import { useSwalDialog } from '@/use/swalDialog';
import { useAddress } from '@/use/address';

import StoreSearch from './Delivery/StoreSearch.vue';
import StoreItems from './Delivery/StoreItems.vue';
import StoreCheckout from './Delivery/StoreCheckout.vue';
import DeliveryItemDetails from '../Dialogs/DeliveryItemDetails.vue';
import { gourmetEstimatePlanList } from '@/constants/deliveryOrder';
import { gourmetGetEstimate, GourmetGetEstimateOutput } from '@/services/gourmet/gourmetGetEstimate';
import { gourmetGetPreEstimate, GourmetGetPreEstimateOutput } from '@/services/gourmet/gourmetGetPreEstimate';
import { getCallerTipGourmet } from '@/services/gourmet/getCallerTipGourmet';
import { ApolloQueryResult } from '@apollo/client/core/types';
import { GetEstimateQuery, GetPreEstimateQuery } from '@/gql/graphql';
import { gourmetService } from '@/services/gourmet';

const defaultChargeOptions = {
  applyDiscountedFee: false,
  noServiceFee: false,
  doNotCharge: false,
  chargeNow: false,
  reason: '',
};
export default {
  name: 'DeliverySearch',

  components: {
    StoreSearch,
    StoreItems,
    StoreCheckout,
    DeliveryItemDetails,
  },

  props: {
    isDeliveryEstimate: {
      type: Boolean,
      default: false,
    },
    visibleStoresLimit: {
      type: Number,
      default: 0,
    },
  },

  emits: ['set-total-stores'],

  setup: (props: any) => {
    const route = useRoute();
    const router = useRouter();

    const { customerInfo, customerPhoneNumber, isNeedToShowFirstDeliveryPopup } = useCustomer();
    const { showFirstDeliveryPopup, savedCart } = useGourmet();
    const { showConfirmPopupDialog, showErrorPopupDialog } = useSwalDialog();
    const { setRegistrationStartAddress } = useAddress();

    const step = ref('search');
    const planName = ref(gourmetEstimatePlanList[1].key);
    const selectedStoreGroup: Ref<any> = ref(null);
    const deliveryAddress: Ref<any> = ref(null);
    const pendingItem: Ref<any> = ref(null);
    const returnToItem: Ref<any> = ref(null);
    const cart: Ref<any> = ref([]);
    const estimate: Ref<any> = ref({});
    const orderOptions: Ref<any> = ref({});
    const item: Ref<any> = ref({});
    const estimatePromisesCanceled: Ref<any> = ref([]);
    const estimatePromise: Ref<any> = ref(null);
    const copyOrder: Ref<any> = ref(null);
    const specialInstructions = ref('');
    const additionalCharges: Ref<any> = ref({
      tip: 0,
    });
    const contactInfo: Ref<any> = ref({
      preferredPhoneNumber: '',
      clientName: '',
    });
    const chargeOptions: Ref<any> = ref(defaultChargeOptions);
    const savedDefaultTip: Ref<any> = ref(undefined);

    const status = reactive({
      optionsSaveError: '',
      isItemUpdate: false,
      isLoadingOptions: false,
      isOptionsModalVisible: false,
      showOptionsErrors: false,
      isEstimateLoading: false,
      estimateFailed: false,
    });

    const resolveOrder = computed(() => {
      if (route.query.resolveOrder) {
        return Array.isArray(route.params.copyId) ? route.params.copyId[0] : route.params.copyId
      }
      return null;
    })
    const estimateMenuIds = computed(() => getMenuIds(estimate.value));

    const isOptionInstructionsDisabled = computed(() => item.value?.item?.isSpecialInstructionsDisabled);

    const paramCopyId = computed(() => route.params.copyId);

    const loadSavedCart = computed(() => route.query.loadSavedCart === 'true');

    const onSelectStore = (storeGroup: any) => {
      clearCart();
      selectedStoreGroup.value = storeGroup;
      status.isEstimateLoading = false;
      step.value = 'store';
    };

    const clearCart = () => {
      cancelEstimate();
      cart.value = [];
      additionalCharges.value.tip = 0;
      contactInfo.value = {
        preferredPhoneNumber: '',
        clientName: '',
      };
      chargeOptions.value = defaultChargeOptions;
      estimate.value = {};
      orderOptions.value = {
        deliveryInstructions: orderOptions.value?.deliveryInstructions,
      };
    };

    const onChangeChargeOptions = (data: any) => {
      if (
        chargeOptions.value.doNotCharge !== data.doNotCharge ||
        chargeOptions.value.chargeNow !== data.chargeNow ||
        chargeOptions.value.noServiceFee !== data.noServiceFee ||
        chargeOptions.value.applyDiscountedFee !== data.applyDiscountedFee
      ) {
        chargeOptions.value = data;
        if (cart.value.length) {
          refreshEstimate();
        }
      } else {
        chargeOptions.value = data;
        estimate.value = {
          ...estimate.value,
          chargeOptions: {
            ...(estimate.value.chargeOptions ?? {}),
            ...data,
          },
        };
      }
    };

    const onChangePlan = (value: string) => {
      planName.value = value;
      refreshEstimate();
    };

    const onSetAddress = (address: any) => {
      deliveryAddress.value = address;
    };

    const onSetDeliveryInstructions = (deliveryInstructions: string) => {
      orderOptions.value.deliveryInstructions = deliveryInstructions;
    };

    const cancelEstimate = () => {
      if (estimatePromise.value) {
        estimatePromisesCanceled.value.push(estimatePromise.value);
      }
      status.isEstimateLoading = false;
      pendingItem.value = null;
    };

    const loadOrderFromEstimate = (storeOrder: any) => {
      const orderEstimate = storeOrder.estimate;
      deliveryAddress.value = orderEstimate.address;
      additionalCharges.value = {
        tip: orderEstimate.additionalCharges.tip / 100,
        shouldTipBeSubsidized: orderEstimate.additionalCharges.shouldTipBeSubsidized
      };

      chargeOptions.value = defaultChargeOptions;
      cart.value = orderEstimate.items;

      delete orderEstimate.callSid;

      if (storeOrder.storeGroup) {
        selectedStoreGroup.value = storeOrder.storeGroup;
      } else {
        selectedStoreGroup.value = {
          title: storeOrder.title,
        };
      }

      estimate.value = orderEstimate;
      orderOptions.value = orderEstimate.orderOptions;
    };

    const loadOrderEstimate = async (orderId: any) => {
      status.isEstimateLoading = true;

      const response = await gourmetService.getOrdersWithEstimation({
        where: {
          id: orderId,
        },
        pagination: {
          limit: 1,
          offset: 0,
        },
      });

      const order = response.rows[0];
      copyOrder.value = order;

      loadOrderFromEstimate(order.storeOrder);

      contactInfo.value = {
        preferredPhoneNumber: order.preferredPhoneNumber,
        clientName: order.clientName,
      };
      status.isEstimateLoading = false;
    };

    const loadOrderFromSavedCart = () => {
      if (!savedCart.value) {
        return;
      }

      loadOrderFromEstimate(savedCart.value);

      contactInfo.value = {
        preferredPhoneNumber: savedCart.value.preferredPhoneNumber,
        clientName: savedCart.value.clientName,
      };
    };

    const refreshEstimate = async () => {
      estimate.value = await getEstimate(cart.value, additionalCharges.value, undefined, null, chargeOptions.value);
    };

    const removeItem = async (item: any) => {
      const index = cart.value.indexOf(item);

      if (index !== -1) {
        cart.value.splice(index, 1);
      }

      await refreshEstimate();
    };

    const showItemOptions = async (itemOption: any, isUpdate: boolean = false) => {
      specialInstructions.value = '';
      status.showOptionsErrors = false;
      status.optionsSaveError = '';
      status.isItemUpdate = isUpdate;
      status.isLoadingOptions = true;
      status.isOptionsModalVisible = true;

      if (itemOption instanceof Promise) {
        itemOption = await itemOption;
      }

      item.value = itemOption;
      item.value.item.specialInstructions = '';
      status.isLoadingOptions = false;
    };

    const optionsFormSubmit = async () => {
      if (pendingItem.value) {
        return;
      }

      const htmlText = `
        <ul>
          <li style="text-align: left;">Please be sure you have selected all the correct ingredients and side items for this order to avoid any disputes</li>
          <li style="text-align: left; padding-top: 10px;">Set proper expectations for special instructions</li>
        </ul>
      `;

      const isConfirmed = await showConfirmPopupDialog({
        text: htmlText,
        title: 'Please Remember',
        confirmButtonText: 'Add Item',
        cancelButtonText: 'Cancel',
        type: 'html',
        icon: 'warning',
      });

      if (!isConfirmed) {
        return;
      }

      const itemValue = item.value;
      const tempCart = cart.value.slice(0);
      status.optionsSaveError = '';

      if (!status.isItemUpdate) {
        tempCart.push(itemValue);
      }

      if (returnToItem.value) {
        item.value = returnToItem.value;
        returnToItem.value = null;
      } else {
        modalClose();
      }

      pendingItem.value = itemValue;
      const promotion = util.getPromotionByStoreId(selectedStoreGroup.value, get(itemValue, 'item.storeId'));
      const getEstimatePromise = getEstimate(
        tempCart,
        additionalCharges.value,
        () => {
          pendingItem.value = null;
          returnToItem.value = item.value?.item || null;
          item.value = itemValue;
          status.showOptionsErrors = true;
          status.isOptionsModalVisible = true;
        },
        promotion,
        chargeOptions.value,
      );

      estimatePromise.value = getEstimatePromise;

      const estimateValue = await estimatePromise.value;

      pendingItem.value = null;
      estimatePromise.value = null;

      if (!estimatePromisesCanceled.value.includes(getEstimatePromise)) {
        estimate.value = estimateValue;
        cart.value = tempCart;
      }
    };

    const getEstimate = async (
      cartItems: any,
      additionalChargeOptions: any,
      afterCloseErrorDialog = () => {},
      promotion: any = null,
      chargeOptionsValue: any = {},
    ) => {
      status.isEstimateLoading = true;
      status.estimateFailed = false;

      await delay(4000);

      return doGetEstimate(cartItems, additionalChargeOptions, afterCloseErrorDialog, promotion, chargeOptionsValue);
    };

    const doGetEstimate = async (
      cartItems: any,
      additionalChargeOptions: any,
      afterCloseErrorDialog = () => {},
      promotion: any = null,
      chargeOptionsValue: any = {},
    ) => {
      if (!promotion && estimate.value && estimate.value.promotion) {
        promotion = estimate.value.promotion;
      }

      const address = { ...deliveryAddress.value };

      if (!deliveryAddress.value.postalCode || deliveryAddress.value.zip) {
        address.postalCode = deliveryAddress.value.zip;
      }

      if (address.zip !== undefined) {
        delete address.zip;
      }

      status.isEstimateLoading = true;
      pendingItem.value = null;
      try {
        let response: ApolloQueryResult<GetPreEstimateQuery | GetEstimateQuery>;
        let estimateValue: GourmetGetEstimateOutput | GourmetGetPreEstimateOutput;

        if (props.isDeliveryEstimate) {
          response = await gourmetGetPreEstimate({
            planName: planName.value,
            items: cartItems,
            address,
            additionalCharges: {
              tip: additionalChargeOptions.tip * 100,
              shouldTipBeSubsidized: additionalChargeOptions.shouldTipBeSubsidized
            },
            chargeOptions: chargeOptionsValue,
            promotion,
          });

          estimateValue = (response as ApolloQueryResult<GetPreEstimateQuery>).data?.getPreEstimate;
        } else {
          response = await gourmetGetEstimate({
            callerId: customerInfo.value.id,
            items: cartItems,
            address,
            additionalCharges: {
              tip: additionalChargeOptions.tip * 100,
              shouldTipBeSubsidized: additionalChargeOptions.shouldTipBeSubsidized
            },
            chargeOptions: chargeOptionsValue,
            promotion,
          });

          estimateValue = (response as ApolloQueryResult<GetEstimateQuery>).data?.getEstimate;
        }

        if (response.errors) {
          const errorText = util.getGraphqlError(
            {
              graphQLErrors: response.errors,
            },
            false,
          );

          ElMessageBox.alert(errorText, '', {
            confirmButtonText: 'Close',
            type: 'error',
            callback: afterCloseErrorDialog,
            showClose: false,
          });

          status.estimateFailed = true;
          status.isEstimateLoading = false;

          return {};
        }

        if (getMenuIds(estimateValue).length > 1) {
          showErrorPopupDialog({
            text: 'This order contains items from different menus. Please clear the cart and pick items again.',
          });
        }

        status.isEstimateLoading = false;
        return estimateValue;
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        ElMessageBox.alert(errorText, '', {
          confirmButtonText: 'Close',
          type: 'error',
          callback: afterCloseErrorDialog,
          showClose: false,
        });

        status.estimateFailed = true;
        status.isEstimateLoading = false;
        return {};
      }
    };

    const modalClose = () => {
      status.isOptionsModalVisible = false;
      item.value = {};
    };

    const onClearStore = () => {
      step.value = 'search';
      selectedStoreGroup.value = null;

      if (copyOrder.value) {
        copyOrder.value = null;
        router.push({
          name: 'DeliverySearch',
          query: {
            phoneNumber: route.query.phoneNumber,
          },
        });
      }
    };

    const getMenuIds = (estimateValue: any) => {
      return uniq(map(estimateValue?.items || [], 'item.serviceItemReference.menuId'));
    };

    const isItemFromTheSameMenu = (itemMenuId: string) => {
      return estimate.value && estimateMenuIds.value.includes(itemMenuId);
    };

    watch(item, (value: any) => {
      const itemMenuId = get(value, 'item.serviceItemReference.menuId');

      if (itemMenuId && estimateMenuIds.value.length && !isItemFromTheSameMenu(itemMenuId)) {
        status.isOptionsModalVisible = false;
        showErrorPopupDialog({
          text: 'This item from another menu. Please clear the cart and pick items again.',
        });
      }
    });

    watch(
      additionalCharges,
      () => {
        if (cart.value.length) {
          refreshEstimate();
        }
      },
      {
        deep: true,
      },
    );

    watch(paramCopyId, (value: any) => {
      if (value) {
        step.value = 'checkout';
        loadOrderEstimate(value);
      }
    });

    watch(isNeedToShowFirstDeliveryPopup, showFirstDeliveryPopup, {
      immediate: true,
    });


    watch(
      () => customerInfo.value?.id,
      async (id) => {
        if (id) {
          savedDefaultTip.value = await getCallerTipGourmet({ callerId: String(id) })
        }
      },
      { immediate: true },
    );

    watch(deliveryAddress, (value: any) => {
      if (props.isDeliveryEstimate && value) {
        setRegistrationStartAddress({
          ...value,
          category: 'home',
        });
      }
    });

    onMounted(() => {
      const callerPhoneNumber = route.query.phoneNumber || '';
      if (callerPhoneNumber && callerPhoneNumber !== customerPhoneNumber.value) {
        // redirected from orders page with 're-order' button
        events.emit('refresh-right-panel');
      }

      if (paramCopyId.value) {
        step.value = 'checkout';
        loadOrderEstimate(paramCopyId.value);
      } else if (loadSavedCart.value) {
        step.value = 'checkout';
        loadOrderFromSavedCart();
      }
    });

    return {
      defaultChargeOptions,
      step,
      selectedStoreGroup,
      deliveryAddress,
      pendingItem,
      returnToItem,
      cart,
      estimate,
      orderOptions,
      item,
      estimatePromisesCanceled,
      estimatePromise,
      copyOrder,
      specialInstructions,
      additionalCharges,
      chargeOptions,
      savedDefaultTip,
      status,
      estimateMenuIds,
      isOptionInstructionsDisabled,
      paramCopyId,
      contactInfo,
      planName,
      util,
      resolveOrder,

      onSelectStore,
      clearCart,
      onChangeChargeOptions,
      onSetAddress,
      onSetDeliveryInstructions,
      cancelEstimate,
      loadOrderEstimate,
      refreshEstimate,
      removeItem,
      showItemOptions,
      optionsFormSubmit,
      getEstimate,
      doGetEstimate,
      modalClose,
      onClearStore,
      getMenuIds,
      isItemFromTheSameMenu,
      onChangePlan,
    };
  },
};
</script>

<style lang="scss">
.delivery-search {
  .dialog-body div {
    text-align: left;
  }

  .item-quantity {
    border-top: 1px solid #ccc;
    padding-top: 10px;
    margin-bottom: 10px;
  }

  .item-options {
    .dialog-body {
      min-height: 50px;

      .el-loading-spinner {
        text-align: center;
      }
    }
    .item-header {
      border-bottom: 1px solid #ccc;
    }
    button.el-button.el-button--success {
      .el-loading-spinner {
        margin-top: -10px;
      }
      .el-loading-mask {
        bottom: -10px;
      }
    }
  }
  .el-overlay .el-dialog__body .option-instruction {
    textarea {
      width: 100%;
      max-width: 500px;
      height: 100px;
      margin-bottom: 10px;
    }
  }
}
</style>
